import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { BasketViewModel } from '../models/BasketViewModel';
import type { CancelPromotionRequestViewModel } from '../models/CancelPromotionRequestViewModel';
import type { EmptyResponseModel } from '../models/EmptyResponseModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { UnlockBasketModel } from '../models/UnlockBasketModel';
import type { UnregisteredConsentRequesViewModel } from '../models/UnregisteredConsentRequesViewModel';
import type { UpdateBasketUserIdRequestViewModel } from '../models/UpdateBasketUserIdRequestViewModel';
import type { ValidatePromotionRequestViewModel } from '../models/ValidatePromotionRequestViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class BasketsService {
  /**
   * Get
   * Get basket
   * @returns BasketViewModel Success
   * @throws ApiError
   */
  public static async getBasket(
    {
      basketId,
      isArchived,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Is archived
       */
      isArchived?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BasketViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BasketViewModel>({
      method: 'GET',
      url: `/baskets`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(isArchived ? { isArchived: isArchived } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Promo Code
   * Validates basket promo code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async validatePromocode(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      body: ValidatePromotionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/baskets/promocode/validate`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cancel Promo Code
   * Cancels promo code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async cancelPromoCode(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      body: CancelPromotionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/baskets/promocode/cancel`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Remove Item
   * Removes an item by basket and item identifier
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async remove(
    {
      basketId,
      itemId,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Item Id
       */
      itemId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/baskets/remove`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Unlock Basket
   * Unlocks current basket
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async unlock(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      body: UnlockBasketModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/baskets/unlock`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update TextVersionId
   * Updates text version id on basket
   * @returns EmptyResponseModel Success
   * @throws ApiError
   */
  public static async updateTextVersionId(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      body: UnregisteredConsentRequesViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseModel>({
      method: 'POST',
      url: `/baskets/unregisteredconsent`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update Basket
   * Updates basket user id
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async updateBasketUserId(
    {
      basketId,
      authorization,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Authorize token
       */
      authorization: string;
      body: UpdateBasketUserIdRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PUT',
      url: `/baskets/update`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
